import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, TextField, Typography, Paper, IconButton, CircularProgress } from '@material-ui/core';
import { SettingsInputAntenna, ExitToApp, SwapVert } from '@material-ui/icons';
import moment from 'moment';
import './ControlTimeContainer.css';
import { getAddressFromCoords } from '../services/getAddressFromCoords';
import { addTimeRecordUseCase } from '../services/addTimeRecordUseCase';
import { getTimeRecordsForDayUseCase } from '../services/getTimeRecordsForDayUseCase';
import { getEmployeeByDniUseCase } from '../services/getEmployeeByDniUseCase';

class ControlTimeContainer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isStandby: true,
            currentUser: null,
            employeeId: null,
            authMethod: null,
            dni: '',
            password: '',
            address: '',
            locationCoords: null,
            history: [],
            predictedAction: 'entrada',
            message: '',
            error: '',
            cooldownUntil: 0,
            inactivityTimer: null,
            loadingRecords: false,
            authenticating: false,
        };
    }

    componentDidMount() {
        // Check for 'id' in query params to identify user
        const urlParams = new URLSearchParams(window.location.search);
        const userId = urlParams.get('id');

        if (userId) {
            // If ID is provided, identify user immediately
            this.handleAutoLogin(userId);
        }

        // Add global click listener to reset logout timer
        document.addEventListener('mousedown', this.resetInactivityTimer);
        document.addEventListener('keypress', this.resetInactivityTimer);
    }

    componentWillUnmount() {
        this.clearInactivityTimer();
        document.removeEventListener('mousedown', this.resetInactivityTimer);
        document.removeEventListener('keypress', this.resetInactivityTimer);
    }

    fetchLocation = () => {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(async (position) => {
                const { latitude, longitude } = position.coords;
                const addr = await getAddressFromCoords(latitude, longitude);
                this.setState({
                    address: addr,
                    locationCoords: { lat: latitude, lng: longitude }
                });
            }, (error) => {
                console.error("Error getting location", error);
                this.setState({ address: 'Ubicación no disponible' });
            });
        }
    }

    loadTodayRecords = async (employeeId) => {
        this.setState({ loadingRecords: true });
        try {
            const records = await getTimeRecordsForDayUseCase(employeeId);
            const history = records.map(record => ({
                type: record.actionType,
                time: moment(record.timestamp).format('HH:mm'),
                timestamp: record.timestamp
            }));
            this.setState({ history, loadingRecords: false });
            this.predictNextAction();
        } catch (error) {
            console.error('Error loading records:', error);
            this.setState({ loadingRecords: false });
        }
    }

    handleAutoLogin = async (userId) => {
        this.setState({ authenticating: true, error: '' });

        try {
            // Validate employee exists
            const employee = await getEmployeeByDniUseCase(userId);

            if (!employee) {
                this.setState({
                    error: 'Empleado no encontrado. Verifique su identificación.',
                    authenticating: false,
                    isStandby: true
                });
                // Clear query params and return to standby
                window.history.replaceState({}, '', window.location.pathname);
                return;
            }

            // Employee found, proceed with login
            this.setState({
                currentUser: {
                    name: employee.name,
                    dni: employee.dni,
                    position: employee.position || 'Operario'
                },
                employeeId: userId,
                authMethod: 'rfid',
                address: 'Obteniendo ubicación...',
                isStandby: false,
                error: '',
                authenticating: false
            }, () => {
                this.startInactivityTimer();
                this.loadTodayRecords(userId);
                this.fetchLocation();
            });
        } catch (error) {
            console.error('Error during auto login:', error);
            this.setState({
                error: 'Error al validar empleado. Intente de nuevo.',
                authenticating: false,
                isStandby: true
            });
            window.history.replaceState({}, '', window.location.pathname);
        }
    }

    handleManualLogin = async () => {
        if (!this.state.dni || !this.state.password) {
            this.setState({ error: 'Por favor ingrese DNI y contraseña' });
            return;
        }

        this.setState({ authenticating: true, error: '' });
        const employeeId = this.state.dni;

        try {
            // Fetch employee from Firebase
            const employee = await getEmployeeByDniUseCase(employeeId);

            if (!employee) {
                this.setState({
                    error: 'DNI no encontrado',
                    authenticating: false
                });
                return;
            }

            // Validate password
            if (employee.password !== this.state.password) {
                this.setState({
                    error: 'Contraseña incorrecta',
                    authenticating: false
                });
                return;
            }

            // Authentication successful
            this.setState({
                currentUser: {
                    name: employee.name,
                    dni: employee.dni,
                    position: employee.position || 'Operario Especialista'
                },
                employeeId,
                authMethod: 'manual',
                address: 'Obteniendo ubicación...',
                isStandby: false,
                message: '',
                error: '',
                dni: '',
                password: '',
                authenticating: false
            }, () => {
                this.startInactivityTimer();
                this.loadTodayRecords(employeeId);
                this.fetchLocation();
            });
        } catch (error) {
            console.error('Error during manual login:', error);
            this.setState({
                error: 'Error al validar credenciales. Intente de nuevo.',
                authenticating: false
            });
        }
    }

    startInactivityTimer = () => {
        this.clearInactivityTimer();
        if (!this.state.isStandby) {
            const timer = setTimeout(() => {
                this.setStandby(true);
            }, 30000); // 30 seconds
            this.setState({ inactivityTimer: timer });
        }
    }

    clearInactivityTimer = () => {
        if (this.state.inactivityTimer) {
            clearTimeout(this.state.inactivityTimer);
        }
    }

    resetInactivityTimer = () => {
        if (!this.state.isStandby) {
            this.startInactivityTimer();
        }
    }

    setStandby = (isStandby) => {
        if (isStandby) {
            // Clear location query params to reset state and show animation
            window.history.replaceState({}, '', window.location.pathname);
            this.clearInactivityTimer();
        } else {
            this.startInactivityTimer();
        }

        this.setState({
            isStandby,
            currentUser: isStandby ? null : this.state.currentUser,
            employeeId: isStandby ? null : this.state.employeeId,
            authMethod: isStandby ? null : this.state.authMethod,
            dni: '',
            password: '',
            locationCoords: null,
            address: '',
            message: ''
        });
    }

    predictNextAction = () => {
        const { history } = this.state;
        if (history.length === 0) {
            this.setState({ predictedAction: 'entrada' });
        } else {
            const last = history[0];
            this.setState({ predictedAction: last.type === 'entrada' ? 'salida' : 'entrada' });
        }
    }

    handleClocking = async (manualAction = null) => {
        // Prevent clocking while loading records
        if (this.state.loadingRecords) {
            this.setState({ message: "Cargando registros, por favor espere..." });
            return;
        }

        const now = Date.now();
        if (now < this.state.cooldownUntil) {
            this.setState({ message: "Por favor espere, ha fichado dos veces muy seguidas." });
            return;
        }

        const action = manualAction || this.state.predictedAction;
        const timeStr = moment().format('HH:mm');

        // Save to database
        try {
            await addTimeRecordUseCase({
                employeeId: this.state.employeeId,
                actionType: action,
                location: this.state.locationCoords ? {
                    address: this.state.address,
                    lat: this.state.locationCoords.lat,
                    lng: this.state.locationCoords.lng
                } : null,
                authMethod: this.state.authMethod,
                notes: ''
            });

            const newEntry = {
                type: action,
                time: timeStr,
                timestamp: now
            };

            this.setState({
                history: [newEntry, ...this.state.history],
                cooldownUntil: now + 60000,
                message: `${action === 'entrada' ? 'Entrada' : 'Salida'} registrada correctamente – ${timeStr}`,
                predictedAction: action === 'entrada' ? 'salida' : 'entrada'
            });
        } catch (error) {
            console.error('Error saving time record:', error);
            this.setState({ message: 'Error al guardar el fichaje. Intente de nuevo.' });
        }

        // Reset inactivity timer on interaction
        this.resetInactivityTimer();
    }

    toggleManualAction = () => {
        this.setState({
            predictedAction: this.state.predictedAction === 'entrada' ? 'salida' : 'entrada'
        });
        this.resetInactivityTimer();
    }

    render() {
        const { isStandby, currentUser, predictedAction, history, message, dni, password, address } = this.state;
        const urlParams = new URLSearchParams(window.location.search);
        const hasIdParam = urlParams.has('id');

        if (isStandby) {
            return (
                <div className="control-time-container">
                    <Paper className="standby-card" elevation={3}>
                        {!hasIdParam ? (
                            <div className="rfid-section">
                                <SettingsInputAntenna style={{ fontSize: 80, color: '#1976d2' }} />
                                <Typography variant="h6" style={{ marginTop: 10 }}>Acerque su tarjeta a la parte superior del teléfono</Typography>
                                <div className="arrow-up">↑</div>

                                <div className="login-divider" style={{ margin: '40px 0 20px', width: '100%', borderTop: '1px solid #eee', position: 'relative' }}>
                                    <span style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '0 15px', color: '#999', fontSize: '0.8rem' }}>O INGRESE MANUALMENTE</span>
                                </div>

                                <div style={{ width: '100%' }}>
                                    {this.state.error && (
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: '#d32f2f',
                                                backgroundColor: '#ffebee',
                                                padding: '10px',
                                                borderRadius: '8px',
                                                marginBottom: '10px',
                                                textAlign: 'center'
                                            }}
                                        >
                                            {this.state.error}
                                        </Typography>
                                    )}
                                    <TextField
                                        label="DNI"
                                        variant="outlined"
                                        fullWidth
                                        margin="dense"
                                        value={dni}
                                        onChange={(e) => this.setState({ dni: e.target.value, error: '' })}
                                        disabled={this.state.authenticating}
                                    />
                                    <TextField
                                        label="Contraseña"
                                        type="password"
                                        variant="outlined"
                                        fullWidth
                                        margin="dense"
                                        value={password}
                                        onChange={(e) => this.setState({ password: e.target.value, error: '' })}
                                        disabled={this.state.authenticating}
                                    />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={this.handleManualLogin}
                                        disabled={this.state.authenticating}
                                        style={{ marginTop: 15, fontWeight: 'bold' }}
                                    >
                                        {this.state.authenticating ? (
                                            <>
                                                <CircularProgress size={20} style={{ color: 'white', marginRight: 10 }} />
                                                VALIDANDO...
                                            </>
                                        ) : (
                                            'ACCEDER'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="rfid-section" style={{ minHeight: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                                {this.state.error ? (
                                    <>
                                        <Typography variant="h6" style={{ color: '#d32f2f', marginBottom: 10 }}>Error</Typography>
                                        <Typography variant="body1" style={{ color: '#666', textAlign: 'center' }}>
                                            {this.state.error}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="h5" color="textSecondary">Identificando...</Typography>
                                )}
                            </div>
                        )}
                    </Paper>
                </div>
            );
        }

        return (
            <div className="control-time-container">
                <div className="active-header">
                    <Typography variant="h5" style={{ fontWeight: 'bold' }}>{currentUser.name}</Typography>
                    <Typography variant="body2" color="textSecondary">{currentUser.dni} • {currentUser.position}</Typography>
                    {address && (
                        <Typography variant="caption" color="primary" style={{ display: 'block', marginTop: 8, fontStyle: 'italic' }}>
                            📍 {address}
                        </Typography>
                    )}
                </div>

                <div className="clocking-controls">
                    <div className="main-action-wrapper">
                        <Button
                            variant="contained"
                            className={`main-clocking-btn ${predictedAction}`}
                            onClick={() => this.handleClocking()}
                            disabled={this.state.loadingRecords}
                        >
                            {this.state.loadingRecords ? (
                                <>
                                    <CircularProgress size={24} style={{ color: 'white', marginRight: 10 }} />
                                    CARGANDO...
                                </>
                            ) : (
                                `CONFIRMAR ${predictedAction.toUpperCase()}`
                            )}
                        </Button>
                        <IconButton
                            className="toggle-action-btn"
                            onClick={this.toggleManualAction}
                            title="Cambiar acción"
                            disabled={this.state.loadingRecords}
                        >
                            <SwapVert fontSize="large" color="primary" />
                        </IconButton>
                    </div>

                    {message && (
                        <div className="status-message">
                            <Typography align="center">{message}</Typography>
                        </div>
                    )}
                </div>

                <div className="history-section">
                    <Typography variant="h6" style={{ paddingLeft: 5, fontWeight: 'bold' }}>Historial de hoy</Typography>
                    <div className="history-list">
                        {history.length === 0 ? (
                            <Paper className="history-item" style={{ justifyContent: 'center' }}>
                                <Typography variant="body2" color="textSecondary">No hay fichajes registrados hoy</Typography>
                            </Paper>
                        ) : (
                            history.map((item, index) => (
                                <Paper key={index} className="history-item">
                                    <span className={`type-badge ${item.type}`}>
                                        {item.type.toUpperCase()}
                                    </span>
                                    <span className="time-val">{item.time}</span>
                                </Paper>
                            ))
                        )}
                    </div>
                </div>

                <div className="exit-footer">
                    <Button
                        variant="outlined"
                        className="secondary-exit-btn"
                        fullWidth
                        startIcon={<ExitToApp />}
                        color="error"
                        onClick={() => this.setStandby(true)}
                    >
                        SALIR / CERRAR SESIÓN
                    </Button>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    userAuthInfo: state.user
})

export default connect(mapStateToProps)(ControlTimeContainer);
